import React, { useState, useEffect } from "react";
import { Doughnut, Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

// Register Chart.js components for doughnut/pie charts
ChartJS.register(ArcElement, Tooltip, Legend);

// Fixed colors in hex format
const fixedColors = [
  "#3D8D7A", // 3D8D7A
  "#B3D8A8", // B3D8A8
  "#A3D1C6", // A3D1C6
  "#4C7B8B", // 4C7B8B
  "#4C7B8B", // 4C7B8B (duplicate for consistency)
];

export default function DoughnutPieChart({
  data,
  type = "doughnut",
  title = "",
}) {
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [validatedData, setValidatedData] = useState([]); // Store validated data for tooltip
  const [selectedLegendIndex, setSelectedLegendIndex] = useState(null); // Track selected legend index

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Dynamically determine the structure based on the first data item
    const firstItem = data[0];
    const dataStructure = Object.keys(firstItem).reduce((acc, key) => {
      acc[key] = typeof firstItem[key] === "number" ? 0 : "";
      return acc;
    }, {});

    // Validate data structure
    const newValidatedData = data.map((item) => ({
      ...dataStructure,
      ...item,
    }));

    // Extract necessary fields for the chart
    const labels = newValidatedData.map((item) => item.dependency);
    const sums = newValidatedData.map((item) => item.sum);

    // Use fixed colors for each segment
    const backgroundColors = fixedColors.slice(0, newValidatedData.length);

    // Prepare the chart data
    const formattedChartData = {
      labels,
      datasets: [
        {
          label: "Cost",
          data: sums,
          backgroundColor: backgroundColors,
          borderWidth: 1,
          borderColor: "#fff",
        },
      ],
    };

    setValidatedData(newValidatedData); // Store for tooltip access
    setChartData(formattedChartData);
    setIsLoading(false);
  }, [data]);

  // Chart options
  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 8,
          font: {
            size: 12,
            family: "Arial",
          },
          color: "#333",
          // Customize legend labels to reflect selection state
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return chart.data.labels.map((label, index) => {
              const dataset = datasets[0];
              const isSelected = selectedLegendIndex === index;
              return {
                text: label,
                fillStyle: dataset.backgroundColor[index],
                hidden: !chart.getDataVisibility(index), // Hide if segment is hidden
                strokeStyle: isSelected ? "#000" : "#fff", // Highlight selected legend
                lineWidth: isSelected ? 2 : 1, // Bold border for selected legend
                fontColor: isSelected ? "#000" : "#333", // Darker text for selected legend
              };
            });
          },
        },
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const index = legendItem.index;

          // Check if the clicked segment is already the only visible one
          const isOnlySegmentVisible =
            chart.data.datasets[0].data.filter((value, i) => {
              return !chart.getDataVisibility(i);
            }).length === 1 && chart.getDataVisibility(index);

          // If the clicked segment is already the only visible one, show all segments
          if (isOnlySegmentVisible) {
            chart.data.datasets[0].data.forEach((_, i) => {
              chart.setDataVisibility(i, true); // Show all segments
            });
            setSelectedLegendIndex(null); // Reset selected legend
          } else {
            // Otherwise, hide all segments except the clicked one
            chart.data.datasets[0].data.forEach((_, i) => {
              chart.setDataVisibility(i, i === index);
            });
            setSelectedLegendIndex(index); // Set selected legend
          }

          chart.update(); // Update the chart to reflect changes
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const index = context.dataIndex;
            const item = validatedData[index];
            return `${item.dependency}: ${item.sum}`;
          },
        },
      },
    },
  };

  // Fixed height for simplicity (adjust as needed)
  const chartHeight = 400;

  // Select chart component based on type prop
  const ChartComponent = type.toLowerCase() === "pie" ? Pie : Doughnut;

  return (
    <div style={{ height: `${chartHeight}px`, width: "100%" }}>
      {title && (
        <h3
          style={{
            textAlign: "center",
            marginBottom: "10px",
            fontFamily: "Arial, sans-serif",
            color: "#333",
          }}
        >
          {title}
        </h3>
      )}
      {isLoading ? (
        <p>Loading chart...</p>
      ) : chartData ? (
        <ChartComponent data={chartData} options={options} />
      ) : (
        <p>No chart data available.</p>
      )}
    </div>
  );
}
